// My Profile (route /more/profile): view and edit the person, not the plan —
// names, username, city, sex, date of birth (age derived), height, nutrition
// preference. Email is shown but not editable (changing it would need a
// re-verification flow that doesn't exist yet — honest label instead).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { useMe, invalidateMe } from "../lib/useMe";
import { useUnits } from "../lib/useUnits";
import { Button } from "../components/ui/Button";
import type { Goals, Sex } from "@shared/types";

const SEXES: Sex[] = ["male", "female", "other"];
const NUTRITION = ["standard", "pescatarian", "vegetarian", "vegan", "other"] as const;

const LABEL = "text-sm text-fg-muted uppercase tracking-wider";

function ageFrom(birthday: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(birthday)) return null;
  const b = new Date(birthday + "T00:00:00");
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return age >= 0 && age <= 130 ? age : null;
}

export function MyProfile() {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const { me } = useMe();
  const units = useUnits();

  const [goals, setGoals] = useState<Goals | null>(null);
  const [loadErr, setLoadErr] = useState(false);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [username, setUsername] = useState("");
  const [city, setCity] = useState("");
  const [sex, setSex] = useState<Sex | "">("");
  const [birthday, setBirthday] = useState("");
  const [heightDisplay, setHeightDisplay] = useState("");
  const [nutrition, setNutrition] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!me) return;
    setFirst(me.name ?? "");
    setLast(me.last_name ?? "");
    setUsername(me.username ?? "");
    setCity(me.city ?? "");
  }, [me]);

  useEffect(() => {
    let cancelled = false;
    api.getGoals()
      .then((g) => {
        if (cancelled) return;
        setGoals(g);
        setSex(g.sex ?? "");
        setBirthday(g.birthday ?? "");
        setHeightDisplay(g.height_cm != null ? String(units.length(g.height_cm).split(" ")[0]) : "");
        setNutrition(g.nutrition_pref ?? "");
      })
      .catch(() => { if (!cancelled) setLoadErr(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const age = birthday ? ageFrom(birthday) : null;
  const lengthUnit = units.prefs.length === "in" ? "in" : "cm";

  async function save() {
    if (saving) return;
    if (!first.trim()) { toast.error(t("profile.first_required")); return; }
    const heightNum = heightDisplay.trim() === "" ? null
      : Math.round(units.parseLength(Number(heightDisplay.replace(",", "."))));
    if (heightNum !== null && (!Number.isFinite(heightNum) || heightNum < 100 || heightNum > 250)) {
      toast.error(t("profile.height_invalid")); return;
    }
    setSaving(true);
    try {
      await api.putProfile({
        name: first.trim(),
        last_name: last.trim() || null,
        username: username.trim() || null,
        city: city.trim() || null,
        sex: sex === "" ? null : sex,
        birthday: birthday || null,
        height_cm: heightNum,
        nutrition_pref: nutrition || null,
      });
      invalidateMe();
      toast.success(t("profile.saved"));
      navigate("/more");
    } catch {
      toast.error(t("common.failed"));
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5 pt-4 animate-rise pb-8">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} aria-label={t("common.back")} className="text-fg-muted text-2xl leading-none -ml-1 press min-w-[44px] min-h-[44px] text-left">‹</button>
        <h1 className="text-title font-bold tracking-tight">{t("profile.title")}</h1>
      </div>

      {loadErr && <p className="text-sm text-danger">{t("error.load_failed")}</p>}

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className={LABEL}>{t("profile.first")}</span>
            <input className="input mt-1" value={first} onChange={(e) => setFirst(e.target.value)} maxLength={60} autoComplete="given-name" />
          </label>
          <label className="block">
            <span className={LABEL}>{t("profile.last")}</span>
            <input className="input mt-1" value={last} onChange={(e) => setLast(e.target.value)} maxLength={60} autoComplete="family-name" />
          </label>
        </div>

        <label className="block">
          <span className={LABEL}>{t("profile.username")}</span>
          <input className="input mt-1" value={username} onChange={(e) => setUsername(e.target.value)} maxLength={30} autoCapitalize="none" autoCorrect="off" placeholder={t("profile.username_ph")} />
        </label>

        {me?.email && (
          <div>
            <span className={LABEL}>{t("auth.email")}</span>
            <div className="input mt-1 flex items-center text-fg-muted">{me.email}</div>
            <p className="mt-1 text-sm text-fg-faint">{t("profile.email_hint")}</p>
          </div>
        )}

        <label className="block">
          <span className={LABEL}>{t("profile.city")}</span>
          <input className="input mt-1" value={city} onChange={(e) => setCity(e.target.value)} maxLength={60} autoComplete="address-level2" />
        </label>

        <div>
          <span className={LABEL}>{t("profile_sheet.sex")}</span>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {SEXES.map((id) => (
              <button key={id} type="button" onClick={() => setSex(id)}
                className={`py-3 rounded-xl text-sm font-medium transition press ${sex === id ? "bg-brand text-on-brand" : "bg-surface text-fg-secondary"}`}>
                {t(`sex.${id}`)}
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <span className={LABEL}>{t("ob.birthday")}</span>
          <input className="input mt-1" type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} />
          {age !== null && (
            <span className="mt-1 block text-sm text-fg-muted">{t("ob.age_is", { n: String(age) })}</span>
          )}
        </label>

        <label className="block">
          <span className={LABEL}>{t("profile_sheet.height")} ({lengthUnit})</span>
          <input className="input mt-1 tabular-nums" type="text" inputMode="decimal" value={heightDisplay} onChange={(e) => setHeightDisplay(e.target.value)} />
        </label>

        <div>
          <span className={LABEL}>{t("profile.nutrition")}</span>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {NUTRITION.map((id) => (
              <button key={id} type="button" onClick={() => setNutrition(nutrition === id ? "" : id)}
                className={`px-3.5 py-2 rounded-full text-sm font-medium press border transition-colors ${nutrition === id ? "bg-brand text-on-brand border-brand" : "bg-surface border-line text-fg-secondary"}`}>
                {t(`nutrition.${id}`)}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-sm text-fg-faint">{t("profile.nutrition_hint")}</p>
        </div>
      </div>

      <Button fullWidth onClick={save} disabled={saving || !goals}>
        {saving ? t("common.loading") : t("common.save")}
      </Button>
    </div>
  );
}
